import { Item, PlacedItem, PickupType, ExtractionPoint, ExtractionSecurity, ExtractionMode, Safehouse, Passphrase } from '../types/game';
import { POI, CityData, NavNode } from '../types/map';
import { RunState } from '../types/run';
import { hasPassive } from './passives';

/** How much to multiply a loot-table weight when a Contacts passive matches. */
const PICKUP_BIAS_FACTOR = 2;

// --- Item definitions ---

export const ITEMS: Record<string, Item> = {
  pistol: {
    id: 'pistol',
    name: 'Pistol',
    category: 'weapon',
    apCost: 0,
    description: 'Ranged attack. Short range (100-200m). Works in mutual sightings.',
    range: 'short',
    sellValue: 40,
  },
  sniperRifle: {
    id: 'sniper-rifle',
    name: 'Sniper Rifle',
    category: 'weapon',
    apCost: 0,
    description: 'Powerful ranged attack. Long range (500m+). Requires long sight line.',
    range: 'long',
    sellValue: 50,
  },
  combatKnife: {
    id: 'combat-knife',
    name: 'Combat Knife',
    category: 'weapon',
    apCost: 0,
    description: 'Silent kill. Must be adjacent and unseen. No signal generated.',
    range: 'melee',
    isSilent: true,
    sellValue: 35,
  },
  lockPick: {
    id: 'lock-pick',
    name: 'Lock Pick Set',
    category: 'tool',
    apCost: 0,
    description: 'Silently enter a locked building. No signal.',
    sellValue: 15,
  },
  burnerPhone: {
    id: 'burner-phone',
    name: 'Burner Phone',
    category: 'gadget',
    apCost: 0,
    description: 'Place a strong distraction signal anywhere on the map. Lasts 3 turns.',
    sellValue: 25,
  },
  binoculars: {
    id: 'binoculars',
    name: 'Binoculars',
    category: 'tool',
    apCost: 0,
    description: 'Passive: doubles vision cone range. On rooftops: 4x range. Reusable.',
    sellValue: 20,
  },
  jammer: {
    id: 'jammer',
    name: 'Jammer',
    category: 'gadget',
    apCost: 0,
    description: '350m signal suppression zone at your position for 3 turns. No signal while inside the zone.',
    sellValue: 30,
  },
  radioScanner: {
    id: 'radio-scanner',
    name: 'Radio Scanner',
    category: 'gadget',
    apCost: 0,
    description: 'Always-on while held: reveals agent passive signals on the map and amplifies them by 25%. Without this (or Echo Chamber / Blown Entrance), you are blind to the agent\'s ambient movement signal.',
    sellValue: 20,
  },
  stimulant: {
    id: 'stimulant',
    name: 'Stimulant',
    category: 'tool',
    apCost: 0,
    description: 'Gain +2 AP this turn only.',
    sellValue: 15,
  },
  bulletproofVest: {
    id: 'bulletproof-vest',
    name: 'Bulletproof Vest',
    category: 'defensive',
    apCost: 0,
    description: 'Absorbs one lethal hit. Position exposed. One use.',
    sellValue: 40,
  },
  flashbang: {
    id: 'flashbang',
    name: 'Flashbang',
    category: 'gadget',
    apCost: 0,
    description: 'Breaks any combat engagement. You escape without damage.',
    sellValue: 30,
  },
};

// Item pool is POI-agnostic. Each pickup spot gets its own randomized table
// of 4 items drawn from the full pool, assigned weights [4,3,2,1] so each
// spot has a common, uncommon, rare, and ultra-rare option. Across the map
// every item is guaranteed to appear at least once.
const ALL_ITEM_KEYS = Object.keys(ITEMS);
const POOL_SIZE = 4;
const POOL_WEIGHTS = [4, 3, 2, 1];

/** Fisher-Yates shuffle (in-place, returns the same array). */
function shuffle<T>(arr: T[]): T[] {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

/**
 * Build a 4-item weighted loot table by pickup type.
 * - 'weapon': ALL slots from the weapon pool only (pistol/sniper/knife).
 *   With only 3 weapons the 4th slot wraps — every weapon is guaranteed.
 * - 'gadget': ALL slots from the gadget+tool pool only (7 items).
 *   Randomly picks 4 of the 7, no non-utility items leak in.
 * - 'mixed':  fully random shuffle of the entire pool (original behavior).
 */
function buildTypedPickupTable(pickupType: PickupType): { item: string; weight: number }[] {
  // Dedicated single-item drops. Lock-pick and radio-scanner are
  // strategically high-value enough that players should be able to plan
  // routes around a guaranteed pickup. A single-entry table guarantees
  // rollFromTable always returns the designated item.
  if (pickupType === 'lockpick') return [{ item: 'lockPick', weight: 1 }];
  if (pickupType === 'scanner') return [{ item: 'radioScanner', weight: 1 }];

  // Lock-pick and radio-scanner are EXCLUDED from the gadget + mixed pools
  // below, so those dedicated drop types are the only way to acquire them.
  // This matches the design goal of making both items planning-friendly
  // rather than chance-dependent.
  const DEDICATED_ONLY = new Set(['lockPick', 'radioScanner']);
  const availableKeys = ALL_ITEM_KEYS.filter(k => !DEDICATED_ONLY.has(k));

  if (pickupType === 'mixed') {
    const pool = shuffle([...availableKeys]);
    return pool.slice(0, POOL_SIZE).map((key, idx) => ({ item: key, weight: POOL_WEIGHTS[idx] }));
  }

  const isMatch = (key: string): boolean => {
    const cat = ITEMS[key]?.category;
    if (pickupType === 'weapon') return cat === 'weapon';
    // gadget pickup: gadgets + tools MINUS the dedicated-only items.
    return cat === 'gadget' || cat === 'tool';
  };

  const pool = shuffle(availableKeys.filter(isMatch));

  // Fill all 4 slots from the matching pool. If the pool is smaller
  // than 4 (weapons = 3), wrap around so every item appears and one
  // gets a duplicate entry with weight redistribution.
  const picked: string[] = [];
  for (let i = 0; i < POOL_SIZE; i++) {
    picked.push(pool[i % pool.length]);
  }

  return picked.map((key, idx) => ({ item: key, weight: POOL_WEIGHTS[idx] }));
}

/**
 * Roll a single item from a weighted loot table.
 *
 * When a Contacts passive (Quartermaster / Gadget Guy / Locksmith's Nephew)
 * is owned, the matching item's weight is multiplied by PICKUP_BIAS_FACTOR
 * *only if that item is already in the table*. Per design: the passive
 * doesn't change what spawns, it just biases which of the available entries
 * you're likely to roll.
 */
function rollFromTable(
  table: { item: string; weight: number }[],
  run?: RunState | null,
): Item {
  const effective = applyPickupPassiveBias(table, run);
  const totalWeight = effective.reduce((sum, entry) => sum + entry.weight, 0);
  let roll = Math.random() * totalWeight;
  for (const entry of effective) {
    roll -= entry.weight;
    if (roll <= 0) {
      const template = ITEMS[entry.item];
      return { ...template, id: `${template.id}-${Math.random().toString(36).slice(2, 8)}` };
    }
  }
  const fallback = ITEMS[effective[0].item];
  return { ...fallback, id: `${fallback.id}-${Math.random().toString(36).slice(2, 8)}` };
}

/** Return a copy of the loot table with Contacts passives biasing weights. */
function applyPickupPassiveBias(
  table: { item: string; weight: number }[],
  run?: RunState | null,
): { item: string; weight: number }[] {
  if (!run || !run.passives || run.passives.length === 0) return table;

  const wantWeapons = hasPassive(run, 'quartermaster');
  const wantGadgets = hasPassive(run, 'gadgetGuy');
  // Locksmith's Nephew was moved from contacts (loot-table bias) to
  // loadout (spawns a Lock Pick in starting inventory). Its pickup bias
  // is no longer applied here — see buildStartingInventory in gameState.ts.
  if (!wantWeapons && !wantGadgets) return table;

  return table.map(entry => {
    const template = ITEMS[entry.item];
    if (!template) return entry;
    let boost = 1;
    if (wantWeapons && template.category === 'weapon') boost *= PICKUP_BIAS_FACTOR;
    if (wantGadgets && template.category === 'gadget') boost *= PICKUP_BIAS_FACTOR;
    return boost === 1 ? entry : { ...entry, weight: entry.weight * boost };
  });
}

const MIN_ITEM_SPACING_M = 160;
// Raised from 40 -> 55. Feedback: drops felt too sparse and often clustered.
// More drops + the new 5-way type split (weapon / gadget / lockpick / scanner
// / mixed, each ~20%) means any side of the map has multiple meaningful
// pickup options regardless of where the grid distribution lands.
const BASE_PICKUP_COUNT = 55;

function haversine(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371000;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/**
 * Build a single placed-item record at a given POI. Factored out so the
 * grid and fallback passes share one construction path.
 */
function buildPlacedItem(
  poi: POI,
  index: number,
  run: RunState | null | undefined,
  pickupType: PickupType,
): PlacedItem {
  const table = buildTypedPickupTable(pickupType);
  return {
    id: `placed-${index}`,
    poi,
    item: rollFromTable(table, run),
    revealed: false,
    collected: false,
    pickupType,
    lootTable: table,
  };
}

// Place items across the map with a grid-based distribution pass so pickups
// are spread out rather than clustered in POI-dense areas. Each spot rolls
// from its own randomized 4-item weighted table. After placement, guarantee
// every item in the global pool appears at least once on the map.
export function placeItems(
  pois: POI[],
  density: number = 0.01,
  run?: RunState | null,
): PlacedItem[] {
  if (pois.length === 0) return [];

  // Black Market Connect: +10 extra pickup locations on the map at round start.
  const blackMarketBonus = hasPassive(run, 'blackMarketConnect') ? 10 : 0;
  const targetCount = Math.max(
    BASE_PICKUP_COUNT + blackMarketBonus,
    ALL_ITEM_KEYS.length,
    Math.floor(pois.length * density) + blackMarketBonus,
  );

  // --- Build a shuffled type queue for pickup distribution ---
  // Five pickup types, base split:
  //   weapon   20%  — cache of pistol/sniper/knife
  //   gadget   20%  — station of gadgets/tools (minus the dedicated items)
  //   lockpick 10%  — guaranteed Lock Pick Set
  //   scanner  10%  — guaranteed Radio Scanner
  //   mixed    40%  — general-supply drops (remainder of the target count)
  //
  // Lockpick + scanner kept at a fixed 10% regardless of active passives:
  // those dedicated drops are what players plan routes around, and
  // shrinking them under a passive would defeat the point. Quartermaster
  // and Gadget Guy shift weapon / gadget shares up; the "mixed" bucket
  // absorbs the shift.
  //
  // Shuffling the combined queue + feeding one type per grid cell (Pass 1
  // below) gives per-type dispersion essentially for free: weapon caches
  // can't cluster on one side because they're individually scattered
  // across the shuffled-cell-order distribution.
  const hasQM = hasPassive(run, 'quartermaster');
  const hasGG = hasPassive(run, 'gadgetGuy');
  let weaponPct = 20, gadgetPct = 20;
  const lockpickPct = 10, scannerPct = 10;
  if (hasQM && hasGG) { weaponPct = 30; gadgetPct = 30; }
  else if (hasQM)     { weaponPct = 30; }
  else if (hasGG)     { gadgetPct = 30; }

  const weaponCount   = Math.round(targetCount * weaponPct / 100);
  const gadgetCount   = Math.round(targetCount * gadgetPct / 100);
  const lockpickCount = Math.round(targetCount * lockpickPct / 100);
  const scannerCount  = Math.round(targetCount * scannerPct / 100);
  // Mixed absorbs the balance so total === targetCount. At baseline shares
  // this works out to ~40% of targetCount.
  const mixedCount    = Math.max(0, targetCount - weaponCount - gadgetCount - lockpickCount - scannerCount);
  const typeQueue: PickupType[] = shuffle([
    ...Array(weaponCount).fill('weapon' as PickupType),
    ...Array(gadgetCount).fill('gadget' as PickupType),
    ...Array(lockpickCount).fill('lockpick' as PickupType),
    ...Array(scannerCount).fill('scanner' as PickupType),
    ...Array(mixedCount).fill('mixed' as PickupType),
  ]);
  let typeIdx = 0;
  const nextType = (): PickupType => typeQueue[typeIdx++] ?? 'mixed';

  const placed: PlacedItem[] = [];
  const usedPoiKeys = new Set<string>();
  const poiKey = (p: POI) => `${p.lat.toFixed(6)},${p.lng.toFixed(6)}`;

  // --- Pass 1: grid-based distribution ---
  // Bucket POIs into a roughly-square grid whose cell count is a little
  // larger than the target, then try to place one pickup per cell. This
  // gives even coverage across the POI-populated area without tight
  // clumping, even when POIs are distributed unevenly.
  let latMin = Infinity, latMax = -Infinity;
  let lngMin = Infinity, lngMax = -Infinity;
  for (const poi of pois) {
    if (poi.lat < latMin) latMin = poi.lat;
    if (poi.lat > latMax) latMax = poi.lat;
    if (poi.lng < lngMin) lngMin = poi.lng;
    if (poi.lng > lngMax) lngMax = poi.lng;
  }
  const latRange = Math.max(1e-9, latMax - latMin);
  const lngRange = Math.max(1e-9, lngMax - lngMin);

  // Slight oversample so every desired cell is likely to have at least one POI.
  const gridN = Math.max(4, Math.ceil(Math.sqrt(targetCount * 1.25)));
  const cells: POI[][] = Array.from({ length: gridN * gridN }, () => []);
  for (const poi of pois) {
    const row = Math.min(gridN - 1, Math.floor(((poi.lat - latMin) / latRange) * gridN));
    const col = Math.min(gridN - 1, Math.floor(((poi.lng - lngMin) / lngRange) * gridN));
    cells[row * gridN + col].push(poi);
  }

  // Visit cells in a shuffled order so we don't bias toward the NE corner
  // when the target is smaller than the grid.
  const cellOrder = cells.map((_, i) => i).sort(() => Math.random() - 0.5);
  for (const idx of cellOrder) {
    if (placed.length >= targetCount) break;
    const cellPois = cells[idx];
    if (cellPois.length === 0) continue;
    // Pick a random POI from the cell for variety between rounds.
    const shuffledCell = [...cellPois].sort(() => Math.random() - 0.5);
    for (const poi of shuffledCell) {
      const key = poiKey(poi);
      if (usedPoiKeys.has(key)) continue;
      const tooClose = placed.some(p =>
        haversine(p.poi.lat, p.poi.lng, poi.lat, poi.lng) < MIN_ITEM_SPACING_M
      );
      if (tooClose) continue;
      placed.push(buildPlacedItem(poi, placed.length, run, nextType()));
      usedPoiKeys.add(key);
      break; // one pickup per cell
    }
  }

  // --- Pass 2: random fallback to hit the target ---
  // If the grid pass left us short (empty cells, tight spacing rejecting
  // candidates, etc.) fill the remainder from the unused POI pool.
  if (placed.length < targetCount) {
    const remaining = pois
      .filter(p => !usedPoiKeys.has(poiKey(p)))
      .sort(() => Math.random() - 0.5);
    for (const poi of remaining) {
      if (placed.length >= targetCount) break;
      const tooClose = placed.some(p =>
        haversine(p.poi.lat, p.poi.lng, poi.lat, poi.lng) < MIN_ITEM_SPACING_M
      );
      if (tooClose) continue;
      placed.push(buildPlacedItem(poi, placed.length, run, nextType()));
      usedPoiKeys.add(poiKey(poi));
    }
  }

  // Resolve a placed item back to its canonical item key (for coverage checks)
  const keyOf = (p: PlacedItem): string => {
    for (const key of ALL_ITEM_KEYS) {
      if (p.item.name === ITEMS[key].name) return key;
    }
    return '';
  };

  // Count occurrences, find missing items, and find duplicates to swap out
  const counts = new Map<string, number>();
  for (const p of placed) {
    const k = keyOf(p);
    counts.set(k, (counts.get(k) ?? 0) + 1);
  }
  const missing = ALL_ITEM_KEYS.filter(k => !counts.has(k));

  // Swap the most-duplicated placed items with the missing ones (random tiebreakers)
  const indices = placed.map((_, i) => i).sort(() => Math.random() - 0.5);
  for (const missingKey of missing) {
    // Find a placed item whose type has count > 1 (we can safely replace it)
    let swapIdx = -1;
    for (const i of indices) {
      const k = keyOf(placed[i]);
      if ((counts.get(k) ?? 0) > 1) {
        swapIdx = i;
        break;
      }
    }
    if (swapIdx === -1) break; // no duplicates left to swap
    const oldKey = keyOf(placed[swapIdx]);
    counts.set(oldKey, (counts.get(oldKey) ?? 1) - 1);
    const template = ITEMS[missingKey];
    placed[swapIdx].item = { ...template, id: `${template.id}-${Math.random().toString(36).slice(2, 8)}` };
    counts.set(missingKey, 1);
  }

  return placed;
}

// Place extraction points. Previously always on the map edge, which made
// every extraction round feel the same: just run to a boundary. Now each
// exit independently rolls edge vs. interior, giving some rounds a city-
// center rendezvous (subway/taxi rendezvous flavour) and others the
// classic airport/port border run.
//
// Constraints:
//   - Always 2 exits (Exit A, Exit B)
//   - Minimum 2 km between the two exits so players can't just hug them
//   - Interior exits must sit outside the outermost 15% of the bounds so
//     they don't pretend-to-be edge ones
//   - Falls back gracefully (edge-only or reduced min-sep) on small maps
//
// Runs at game-state init time, so this is picked up without re-uploading
// the R2 precomputed JSONs.
export function placeExtractions(cityData: CityData): ExtractionPoint[] {
  const { bounds, navigation } = cityData;
  const extractions: ExtractionPoint[] = [];

  // --- Define edge and interior node pools ---
  const latEdgeMargin = (bounds.north - bounds.south) * 0.10;
  const lngEdgeMargin = (bounds.east - bounds.west) * 0.10;
  const latInteriorMargin = (bounds.north - bounds.south) * 0.15;
  const lngInteriorMargin = (bounds.east - bounds.west) * 0.15;

  const edgeNodes = navigation.nodes.filter(n =>
    n.lat <= bounds.south + latEdgeMargin ||
    n.lat >= bounds.north - latEdgeMargin ||
    n.lng <= bounds.west + lngEdgeMargin ||
    n.lng >= bounds.east - lngEdgeMargin,
  );
  const interiorNodes = navigation.nodes.filter(n =>
    n.lat > bounds.south + latInteriorMargin &&
    n.lat < bounds.north - latInteriorMargin &&
    n.lng > bounds.west + lngInteriorMargin &&
    n.lng < bounds.east - lngInteriorMargin,
  );

  if (edgeNodes.length === 0 && interiorNodes.length === 0) return extractions;

  // --- Distance helper in metres (approx, good enough for separation checks) ---
  const distM = (a: NavNode, b: NavNode): number => {
    const dLat = (a.lat - b.lat) * 111000;
    const dLng = (a.lng - b.lng) * 111000 * Math.cos((a.lat * Math.PI) / 180);
    return Math.sqrt(dLat * dLat + dLng * dLng);
  };

  // --- Per-exit: choose a placement pool, then pick the best candidate
  //     (farthest from already-used exits, respecting a 2 km minimum). ---
  const MIN_SEP_M = 2000;
  const MIN_SEP_M_FALLBACK = 1200; // relaxed if map is small
  const targetCount = 2;
  const used: NavNode[] = [];

  // Per-position mode assignments. Edge exits keep their "border" flavour
  // (airport/port/train/road), interior exits lean train/road (subway,
  // city-center pickup) because airport/port don't make sense downtown.
  const edgeModes: ExtractionMode[] = ['airport', 'port', 'train', 'road'];
  const interiorModes: ExtractionMode[] = ['train', 'road'];
  const securitiesByMode: Record<ExtractionMode, ExtractionSecurity> = {
    airport: 'high',
    port: 'medium',
    train: 'medium',
    road: 'low',
  };

  for (let i = 0; i < targetCount; i++) {
    // 50/50 edge vs interior. If one pool is empty, fall back to the other.
    const preferInterior = Math.random() < 0.5;
    let pool = preferInterior ? interiorNodes : edgeNodes;
    let isInterior = preferInterior;
    if (pool.length === 0) {
      pool = preferInterior ? edgeNodes : interiorNodes;
      isInterior = !preferInterior;
    }
    if (pool.length === 0) break;

    // Score candidates: must be ≥ MIN_SEP_M from all prior picks; among
    // valid candidates pick the farthest (best spread). If nothing meets
    // the strict minimum, relax and take the best-available.
    let bestNode: NavNode | null = null;
    let bestMinDist = -1;
    let bestFallbackNode: NavNode | null = null;
    let bestFallbackMinDist = -1;

    const attempts = Math.min(80, pool.length);
    for (let a = 0; a < attempts; a++) {
      const candidate = pool[Math.floor(Math.random() * pool.length)];
      const minDist = used.length === 0
        ? Infinity
        : Math.min(...used.map(u => distM(u, candidate)));
      if (minDist >= MIN_SEP_M && minDist > bestMinDist) {
        bestMinDist = minDist;
        bestNode = candidate;
      }
      if (minDist > bestFallbackMinDist) {
        bestFallbackMinDist = minDist;
        bestFallbackNode = candidate;
      }
    }

    const picked = bestNode ?? (bestFallbackMinDist >= MIN_SEP_M_FALLBACK ? bestFallbackNode : bestFallbackNode);
    if (!picked) break;
    used.push(picked);

    const modePool = isInterior ? interiorModes : edgeModes;
    const mode = modePool[Math.floor(Math.random() * modePool.length)];
    const security = securitiesByMode[mode];

    extractions.push({
      id: `extraction-${i}`,
      name: `Exit ${String.fromCharCode(65 + i)}`,
      lat: picked.lat,
      lng: picked.lng,
      security,
      mode,
    });
  }

  return extractions;
}

// Place 2 safehouses at actual building polygons in the interior of the map, at least 500m apart.
// Picks buildings first, then associates each with the nearest street node for pathfinding.
export function placeSafehouses(cityData: CityData): Safehouse[] {
  const { bounds } = cityData;
  const safehouses: Safehouse[] = [];

  // Collect building centroids from the interior of the map
  const latMargin = (bounds.north - bounds.south) * 0.15;
  const lngMargin = (bounds.east - bounds.west) * 0.15;

  interface BuildingCandidate {
    lat: number;
    lng: number;
    featureIndex: number;
  }
  const candidates: BuildingCandidate[] = [];
  for (let fi = 0; fi < cityData.layers.buildings.features.length; fi++) {
    const feature = cityData.layers.buildings.features[fi];
    if (!feature.geometry || feature.geometry.type !== 'Polygon') continue;
    const coords = (feature.geometry as GeoJSON.Polygon).coordinates[0];
    if (!coords || coords.length < 3) continue;
    let cLat = 0, cLng = 0;
    for (const [lng, lat] of coords) { cLat += lat; cLng += lng; }
    cLat /= coords.length;
    cLng /= coords.length;
    // Interior only
    if (cLat < bounds.south + latMargin || cLat > bounds.north - latMargin) continue;
    if (cLng < bounds.west + lngMargin || cLng > bounds.east - lngMargin) continue;
    candidates.push({ lat: cLat, lng: cLng, featureIndex: fi });
  }

  if (candidates.length === 0) return safehouses;

  // Shuffle and pick 2 with spacing
  const shuffled = [...candidates].sort(() => Math.random() - 0.5);
  const used: BuildingCandidate[] = [];

  for (let i = 0; i < 3; i++) {
    let best: BuildingCandidate | null = null;
    let bestMinDist = -1;
    for (let attempt = 0; attempt < 80 && attempt < shuffled.length; attempt++) {
      const c = shuffled[attempt];
      const minDist = used.length === 0 ? Infinity :
        Math.min(...used.map(u => haversine(u.lat, u.lng, c.lat, c.lng)));
      if (minDist > bestMinDist && (used.length === 0 || minDist >= 500)) {
        bestMinDist = minDist;
        best = c;
      }
    }
    if (!best) best = shuffled.find(c => !used.includes(c)) ?? null;
    if (!best) break;

    used.push(best);
    safehouses.push({
      id: `safehouse-${i}`,
      lat: best.lat,
      lng: best.lng,
      visited: false,
      buildingFeatureId: null, // resolved at runtime via map query
    });
  }

  return safehouses;
}

// WiFi-style passphrase words
const PASSPHRASE_WORDS = [
  'ALPHA', 'BRAVO', 'CIPHER', 'DELTA', 'ECHO', 'FALCON', 'GHOST',
  'HARBOR', 'IRON', 'JADE', 'KILO', 'LANCE', 'MERCURY', 'NOVA',
  'OMEGA', 'PHOENIX', 'QUARTZ', 'RAVEN', 'SIGMA', 'TANGO',
  'ULTRA', 'VORTEX', 'WHISPER', 'XENON', 'YANKEE', 'ZENITH',
  'COBALT', 'DAGGER', 'EMBER', 'FROST', 'GRANITE', 'HYDRA',
];

function generatePassphrase(): string {
  const word = PASSPHRASE_WORDS[Math.floor(Math.random() * PASSPHRASE_WORDS.length)];
  const num = Math.floor(Math.random() * 900) + 100; // 3-digit number
  return `${word}-${num}`;
}

// Place passphrases at POIs, spread across the map with minimum spacing.
// Target is 6 — the extra density gives the player meaningful choice about
// which two to collect (Ship 1's Hit rounds need 2 to retain safehouse
// cards; Extraction rounds need 2 to extract) rather than a forced march
// through every dead drop on the map.
export function placePassphrases(pois: POI[]): Passphrase[] {
  const passphrases: Passphrase[] = [];
  const shuffled = [...pois].sort(() => Math.random() - 0.5);
  const MIN_SPACING = 400; // meters
  const TARGET_COUNT = 6;

  for (const poi of shuffled) {
    if (passphrases.length >= TARGET_COUNT) break;
    const tooClose = passphrases.some(p =>
      haversine(p.lat, p.lng, poi.lat, poi.lng) < MIN_SPACING
    );
    if (tooClose) continue;

    passphrases.push({
      id: `passphrase-${passphrases.length}`,
      lat: poi.lat,
      lng: poi.lng,
      word: generatePassphrase(),
      collected: false,
      poiName: poi.name || poi.category,
    });
  }

  return passphrases;
}
